import { useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import IconToken from '@/components/icons/IconToken'
import IconChevronDown from '@/components/icons/IconChevronDown'
import IconArrowDown from '@/components/icons/IconArrowDown'
import {
  marketMapState,
  marketListState,
  marketTickerMapState,
  marketInfoValue
} from '@/recoil/Market'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import numberFormat from '@/utils/numberFormat'

const MARKET_TYPE = {
  spot: 'SPOT',
  futures: 'PERP'
}

export default function MarketList() {
  const navigate = useNavigate()
  const [marketMap] = useRecoilState(marketMapState)
  const [marketList] = useRecoilState(marketListState)
  const [marketTickerMap] = useRecoilState(marketTickerMapState)
  const marketInfo = useRecoilValue(marketInfoValue)
  const marketInfoReady = Object.keys(marketInfo).length > 0
  const [marketListActive, setMarketListActive] = useState(false)
  const [marketType, setMarketType] = useState(MARKET_TYPE.spot)

  const marketListRef = useRef()
  useOnClickOutside(marketListRef, () => setMarketListActive(false))

  const navigateMarket = useCallback(
    marketSymbol => navigate(`/trade/${marketSymbol}`),
    [navigate]
  )

  if (!marketInfoReady) {
    return null
  }

  const onSetMarketType = (e, marketType) => {
    e.stopPropagation()
    setMarketType(marketType)
  }

  const MarketList = marketListActive
    ? marketList
        .filter(symbol => {
          const isFutures = marketMap[symbol]?.isFutures
          return marketType === MARKET_TYPE.futures ? isFutures : !isFutures
        })
        .map(symbol => {
          if (!marketMap[symbol] || !marketTickerMap[symbol]) {
            return null
          }
          const notCurrentMarket = marketInfo.symbol !== symbol
          const { isFutures, baseCurrency, quoteCurrency, priceDecimal } =
            marketMap[symbol]
          const { open, close } = marketTickerMap[symbol]
          const change = BigNumber(open).minus(close)
          const changePercentage = BigNumber(BigNumber(change).abs())
            .div(open)
            .times(100)
            .toNumber()
          return (
            <div
              key={symbol}
              className="market-list-row"
              onClick={() => notCurrentMarket && navigateMarket(symbol)}
            >
              <div className="market-symbol">
                <IconToken symbol={baseCurrency} />
                {isFutures ? (
                  `${baseCurrency}-PERP`
                ) : (
                  <>
                    {baseCurrency} /{' '}
                    <div className="market-quote-currency">{quoteCurrency}</div>
                  </>
                )}
              </div>
              <div className="market-data">
                <div>{numberFormat(close, priceDecimal)}</div>
                <div
                  className={clsx('price-change', {
                    'color-positive': change.lt(0),
                    'color-negative': change.gt(0)
                  })}
                >
                  {!change.eq(0) && (
                    <IconArrowDown
                      className={clsx('price-change-icon', {
                        'is-up': change.lt(0)
                      })}
                    />
                  )}
                  {numberFormat(changePercentage, 2)}%
                </div>
              </div>
            </div>
          )
        })
    : []

  return (
    <div
      ref={marketListRef}
      className={clsx('market-name', { active: marketListActive })}
      onMouseDown={e => e.stopPropagation()}
      onClick={() => marketInfoReady && setMarketListActive(active => !active)}
    >
      <IconToken symbol={marketInfo.baseCurrency} />
      {marketInfo.isFutures ? (
        `${marketInfo.baseCurrency}-PERP`
      ) : (
        <>
          {marketInfo.baseCurrency} /{' '}
          <div className="market-quote-currency">
            {marketInfo.quoteCurrency}
          </div>
        </>
      )}
      <IconChevronDown className="icon-chevron-down" />
      <div className="market-list">
        <div className="market-type-tab-list">
          <div
            className={clsx('market-type-tab', {
              active: marketType === MARKET_TYPE.spot
            })}
            onClick={e => onSetMarketType(e, MARKET_TYPE.spot)}
          >
            Spot
          </div>
          <div
            className={clsx('market-type-tab', {
              active: marketType === MARKET_TYPE.futures
            })}
            onClick={e => onSetMarketType(e, MARKET_TYPE.futures)}
          >
            Futures
          </div>
        </div>
        <div className="market-list-row-wrap">{MarketList}</div>
      </div>
    </div>
  )
}
